import React, { useEffect, useMemo, useState } from 'react'
import { t } from 'src/translations'

import { ProfileAddresses } from 'public/components/default_template/online_ordering/account/profile_addresses/ProfileAddresses'
import { AccountInformation } from 'public/components/default_template/online_ordering/account/profile_information/AccountInformation'
import { AccountSettings } from 'public/components/default_template/online_ordering/account/AccountSettings'
import { LoggedOutAccountState } from 'public/components/default_template/online_ordering/account/LoggedOutAccountState'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { useGuestQuery } from 'src/apollo/onlineOrdering'
import SavedPaymentMethods from './SavedPaymentMethods'
import ToastCash from './ToastCash'
import { Modal, ModalOverlay, useModal } from 'shared/components/common/modal'
import LoadingSpinnerOverlay from 'src/shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import {
  LoginStage,
  PwlessAuth
} from 'public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth'
import { AuthenticationSource } from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import ToastAccountValueProp from 'public/components/default_template/online_ordering/account/ToastAccountValueProp'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'

const Account = () => {
  const { customer, refetchCustomer } = useCustomer()
  const {
    data: guestData,
    loading: guestDataLoading,
    refetch: refetchGuestData
  } = useGuestQuery({ skip: !customer })
  const { track } = useThrottledTracker()
  // a logged out account screen is required for branded app acceptance to Google Play Store
  const isLoggedOut = useMemo(() => !customer, [customer])
  const { isOpen, onClose, onOpen } = useModal()
  const [isLoadingEmailVerification, setIsLoadingEmailVerification] =
    useState(false)
  const isIntlRestaurant = useIsIntlRestaurant()

  const isLoading = useMemo(() => {
    return guestDataLoading || isLoadingEmailVerification
  }, [guestDataLoading, isLoadingEmailVerification])

  // refetch the customer to get the latest data
  useEffect(() => {
    refetchCustomer()
  }, [refetchCustomer])

  useEffect(() => {
    if (!isLoggedOut && !guestData) {
      refetchGuestData()
    }
  }, [isLoggedOut, refetchGuestData, guestData])

  // Send tracking once customer data is available
  useEffect(() => {
    if (!guestDataLoading) {
      track(TrackingEvents.ACCOUNT_PAGE_VIEW, {
        isAuthenticated: !isLoggedOut,
        ...(!isLoggedOut
          ? {
              hasSavedAddresses: !!customer?.addresses?.length,
              hasWalletPaymentMethods: !!customer?.creditCards?.length,
              hasEmailVerified:
                guestData?.guest.contact?.contactEmails?.[0]?.isVerified
            }
          : {})
      })
    }
  }, [customer, guestDataLoading, guestData, track, isLoggedOut])

  return (
    <main
      className='account'
      aria-label={t('account.aria-label')}
      tabIndex={-1}
      id='main'
    >
      {isLoading && <LoadingSpinnerOverlay />}
      <Modal isOpen={isOpen} onClose={onClose} preventOverlayClose>
        <ModalOverlay fadeIn fadeOut style={{ backdropFilter: 'blur(6px)' }} />
        <PwlessAuth
          source={AuthenticationSource.AccountPage}
          currentLoginStage={LoginStage.LoggedOutAccountPage}
        />
      </Modal>
      {isLoggedOut ? (
        <LoggedOutAccountState onDeleteAccountClick={onOpen} />
      ) : (
        <>
          <AccountInformation
            contact={guestData?.guest.contact}
            setIsLoadingEmailVerification={setIsLoadingEmailVerification}
            refetchGuestData={refetchGuestData}
          />
          <div className='accountSections' data-testid='Account'>
            <div className='desktopLeft'>
              <ProfileAddresses />
              <SavedPaymentMethods />
            </div>
            <div className='desktopRight'>
              {!isIntlRestaurant && <ToastCash />}
              <AccountSettings />
              <ToastAccountValueProp />
            </div>
          </div>
        </>
      )}
    </main>
  )
}

export default Account
